/**
 * WordPress dependencies
 */
import { registerBlockBindingsSource } from '@wordpress/blocks';
import { __ } from '@wordpress/i18n';
import { store as coreDataStore } from '@wordpress/core-data';

/**
 * Get list of available fields for the team member source.
 * This provides the dropdown options in the block bindings UI.
 *
 * @param {Object} options         Options object.
 * @param {Object} options.context Block context (postId, postType, etc.).
 * @return {Array} Array of field objects with label, type, and args.
 */
function getFieldsList( { context } ) {
	// Only show fields for team member post type.
	const postType = context?.postType;
	if ( ! postType || postType !== 'bookable_team_member' ) {
		return [];
	}

	return [
		{
			label: __( 'Team member name', 'woocommerce-bookings' ),
			type: 'string',
			args: { field: 'name' },
		},
		{
			label: __( 'Team member email', 'woocommerce-bookings' ),
			type: 'string',
			args: { field: 'email' },
		},
		{
			label: __( 'Team member phone number', 'woocommerce-bookings' ),
			type: 'string',
			args: { field: 'phone_number' },
		},
		{
			label: __( 'Team member description', 'woocommerce-bookings' ),
			type: 'string',
			args: { field: 'description' },
		},
	];
}

/**
 * Register the team member block bindings source with UI support.
 */
registerBlockBindingsSource( {
	name: 'woocommerce-bookings/team-member',
	label: __( 'Team Member', 'woocommerce-bookings' ),
	getFieldsList,
	getValues: ( { bindings, select, context } ) => {
		const field = bindings.content?.args?.field;
		const store = select( coreDataStore );
		const record = store.getEntityRecord(
			'root',
			'team_member',
			context.postId
		);

		if ( ! record ) {
			let placeholder = '';
			switch ( field ) {
				case 'name':
					placeholder = __( 'Name', 'woocommerce-bookings' );
					break;
				case 'email':
					placeholder = __( 'Email', 'woocommerce-bookings' );
					break;
				case 'phone_number':
					placeholder = __( 'Phone number', 'woocommerce-bookings' );
					break;
				case 'description':
					placeholder = __( 'Description', 'woocommerce-bookings' );
					break;
				default:
					placeholder = __(
						'Team Member Data',
						'woocommerce-bookings'
					);
					break;
			}

			return {
				content: placeholder,
			};
		}

		// Proceed to live preview.
		switch ( field ) {
			case 'name':
				return {
					content: record.name,
				};
			case 'email':
				return {
					content: record.email,
				};
			case 'phone_number':
				return {
					content: record.phone_number,
				};
			case 'description':
				return {
					content: record.description,
				};
			default:
				return {
					content: record.name,
				};
		}
	},
	usesContext: [ 'postId', 'postType' ],
} );
